import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';

export const OT_TYPE_LABELS = {
  normal_day: { label: 'Normal Working Day (1.5× HRP)', color: 'text-blue-600 dark:text-blue-400' },
  off_day: { label: 'Off Day (1.5× HRP)', color: 'text-teal-600 dark:text-teal-400' },
  rest_day: { label: 'Rest Day (ORP-based)', color: 'text-purple-600 dark:text-purple-400' },
  public_holiday: { label: 'Public Holiday (ORP-based)', color: 'text-red-600 dark:text-red-400' },
};

// Asks the backend to classify the day for THIS employee (rest day / off day / PH / normal) and preview the pay.
export const useOvertimePreview = ({ employee_id, date, start_time, end_time }, enabled) => {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!enabled || !employee_id || !date) { setPreview(null); return undefined; }
    let active = true;
    const t = setTimeout(() => {
      axios.get(`${API}/overtime/preview`, { params: { employee_id, date, start_time, end_time } })
        .then((res) => { if (active) setPreview(res.data); })
        .catch(() => { if (active) setPreview(null); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [employee_id, date, start_time, end_time, enabled]);
  return preview;
};

export const OvertimePreviewCard = ({ preview, testId = 'ot-preview' }) => {
  if (!preview) {
    return <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Select an employee and date — the day type is derived from the calendar and the employee's designated rest day.</p>;
  }
  const t = OT_TYPE_LABELS[preview.day_type] || OT_TYPE_LABELS.normal_day;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-xs space-y-1" data-testid={testId}>
      <p className={`font-semibold ${t.color}`} data-testid={`${testId}-type`}>{t.label}</p>
      <p className="text-slate-600 dark:text-slate-300" data-testid={`${testId}-reason`}>{preview.reason}</p>
      <p className="text-slate-500 dark:text-slate-400">
        Rest day: <span className="font-medium">{preview.rest_day?.charAt(0).toUpperCase() + preview.rest_day?.slice(1)}</span> · ORP RM {Number(preview.orp || 0).toFixed(2)} · HRP RM {Number(preview.hourly_rate || 0).toFixed(2)}
      </p>
      {preview.hours > 0 && (
        <p className="text-slate-800 dark:text-slate-100" data-testid={`${testId}-pay`}>
          {preview.hours}h → <span className="font-bold text-emerald-600">RM {Number(preview.overtime_pay || 0).toFixed(2)}</span>
          <span className="block text-[11px] text-slate-500 dark:text-slate-400">{preview.calculation}</span>
        </p>
      )}
    </div>
  );
};

export const OvertimeRateTable = () => (
  <div className="overflow-x-auto" data-testid="ot-rate-table">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-blue-800 dark:text-blue-300">
          <th className="py-1 pr-3 font-semibold">Day Classification</th>
          <th className="py-1 pr-3 font-semibold">Working Hours</th>
          <th className="py-1 font-semibold">Payment Rate</th>
        </tr>
      </thead>
      <tbody className="text-blue-900 dark:text-blue-200">
        <tr><td className="py-0.5 pr-3">Normal Working Day</td><td className="py-0.5 pr-3">Beyond normal working hours</td><td className="py-0.5">1.5 × HRP per additional hour</td></tr>
        <tr><td className="py-0.5 pr-3">Off Day (non-working, not the rest day)</td><td className="py-0.5 pr-3">Any hours worked</td><td className="py-0.5">1.5 × HRP per hour</td></tr>
        <tr><td className="py-0.5 pr-3">Rest Day (employee's designated day)</td><td className="py-0.5 pr-3">Up to 4 hours</td><td className="py-0.5">0.5 × ORP</td></tr>
        <tr><td className="py-0.5 pr-3">Rest Day</td><td className="py-0.5 pr-3">More than 4 hours, up to 8 hours</td><td className="py-0.5">1.0 × ORP</td></tr>
        <tr><td className="py-0.5 pr-3">Rest Day</td><td className="py-0.5 pr-3">Exceeding 8 hours</td><td className="py-0.5">1.0 × ORP + 2.0 × HRP per additional hour</td></tr>
        <tr><td className="py-0.5 pr-3">Public Holiday</td><td className="py-0.5 pr-3">Within normal working hours (≤ 8)</td><td className="py-0.5">2.0 × ORP</td></tr>
        <tr><td className="py-0.5 pr-3">Public Holiday</td><td className="py-0.5 pr-3">Exceeding 8 hours</td><td className="py-0.5">2.0 × ORP + 3.0 × HRP per additional hour</td></tr>
      </tbody>
    </table>
  </div>
);
